'use client'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface SimplePDFDrawerProps {
  isOpen: boolean
  onClose: () => void
  page?: string
  documentName?: string
  language?: string
}

/**
 * Drawer semplificato per visualizzare PDF senza interferire con il chatbot
 */
export function SimplePDFDrawer({
  isOpen,
  onClose,
  page = '1',
  documentName = 'Guida-dello-Studente-2025_2026.pdf',
  language = 'it',
}: SimplePDFDrawerProps) {
  const pdfUrl = `/pdf/${documentName}#page=${page}&language=${language}`
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Gestisce le animazioni di apertura/chiusura
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      // Piccolo delay per permettere al DOM di renderizzare
      setTimeout(() => setIsAnimating(true), 10)
    } else {
      setIsAnimating(false)
      // Aspetta che l'animazione finisca prima di rimuovere dal DOM
      setTimeout(() => setIsVisible(false), 300)
    }
  }, [isOpen])

  // Gestisce la chiusura con ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isVisible) return null

  return (
    <>
      {/* Overlay leggero solo per il drawer */}
      <div
        className={cn(
          'fixed inset-0 bg-black/20 z-40 transition-opacity duration-300 ease-in-out',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed left-0 top-0 h-full w-[60vw] sm:max-w-2xl',
          'bg-white border-r shadow-lg z-50',
          'transform transition-transform duration-300 ease-out',
          isAnimating ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between p-4 border-b bg-white transition-opacity duration-200 delay-100',
            isAnimating ? 'opacity-100' : 'opacity-0'
          )}
        >
          <h2 className="text-lg font-semibold">
            {documentName} - {language === 'it' ? 'pagina' : 'page'} - {page}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div
          className={cn(
            'h-[calc(100vh-80px)] w-full transition-opacity duration-200 delay-150',
            isAnimating ? 'opacity-100' : 'opacity-0'
          )}
        >
          <iframe
            src={pdfUrl}
            className="w-full h-full border-0"
            title="Visualizzatore PDF"
            key={pdfUrl}
          />
        </div>
      </div>
    </>
  )
}
